import { Component, OnInit, Input } from '@angular/core';
import { ConfigurationService } from '../../../configurationService';
import { MessageService } from 'primeng/api';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-wb-column-settings',
  templateUrl: './wb-column-settings.component.html',
  styleUrls: ['./wb-column-settings.component.scss']
})
export class WbColumnSettingsComponent implements OnInit {
  @Input() isActive: boolean = false;
  settings: any[] = [];
  loading: boolean = false;
  moduleId: number = 355;
  settingsForm: FormGroup;
  editingRows: { [key: string]: boolean } = {};
  savingRows: { [key: string]: boolean } = {};  // Track saving state per row
  originalValues: { [key: string]: any } = {};

  constructor(
    private configurationService: ConfigurationService,
    private messageService: MessageService,
    private fb: FormBuilder
  ) { }

  ngOnInit() {
    this.settingsForm = this.fb.group({});
  }

  ngOnChanges() {
    if (this.isActive && this.settings.length === 0) {
      this.loadSettings();
    }
  }

  loadSettings() {
    this.loading = true;
    this.configurationService.getPhoneNumberTemplate(this.moduleId).subscribe({
      next: (response) => {
        this.settings = response;
        this.initializeForm();
        this.loading = false;
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load column settings'
        });
        this.loading = false;
      }
    });
  }

  initializeForm() {
    const formGroup = {};
    this.settings.forEach(setting => {
      const validators = [Validators.required, Validators.min(1)];
      
      if (setting.code?.toLowerCase().includes('kanban')) {
        validators.push(Validators.max(50));
      } else if (setting.code?.toLowerCase().includes('list')) {
        validators.push(Validators.max(200));
      }
      
      formGroup[setting.code] = [{ value: setting.value, disabled: true }, validators];
      this.originalValues[setting.code] = setting.value;
    });
    
    this.settingsForm = this.fb.group(formGroup);
  }

  getMaxLimit(setting: any): number {
    if (setting.code?.toLowerCase().includes('kanban')) {
      return 50;
    } else if (setting.code?.toLowerCase().includes('list')) {
      return 200;
    }
    return 999999;
  }

  getSuggestedValueTooltip(setting: any): string {
    if (setting.code?.toLowerCase().includes('kanban')) {
      return 'Suggested value: 6 columns for optimal Kanban view display';
    } else if (setting.code?.toLowerCase().includes('list')) {
      return 'Suggested value: 12 columns for optimal List view display';
    }
    return '';
  }

  getErrorMessage(setting: any): string {
    const control = this.settingsForm.get(setting.code);
    if (control?.errors) {
      if (control.errors['required']) return 'Value is required';
      if (control.errors['min']) return 'Value must be greater than 0';
      if (control.errors['max']) {
        const max = this.getMaxLimit(setting);
        return `Value cannot exceed ${max}`;
      }
    }
    return '';
  }

  toggleEdit(setting: any) {
    const code = setting.code;
    if (this.editingRows[code]) {
      // Cancel edit - restore original value
      this.settingsForm.get(code)?.setValue(this.originalValues[code]);
      this.settingsForm.get(code)?.disable();
    } else {
      // Start edit
      this.settingsForm.get(code)?.enable();
    }
    this.editingRows[code] = !this.editingRows[code];
  }

  hasChanges(setting: any): boolean {
    const currentValue = this.settingsForm.get(setting.code)?.value;
    return currentValue !== this.originalValues[setting.code];
  }

  saveSetting(setting: any) {
    if (this.settingsForm.get(setting.code)?.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please check the field for errors'
      });
      return;
    }

    const update = {
      code: setting.code,
      enabled: setting.enabled,
      moduleId: this.moduleId,
      value: this.settingsForm.get(setting.code)?.value
    };

    this.savingRows[setting.code] = true;  // Set saving state for this row
    this.configurationService.updateCrmSetting(update).subscribe({
      next: () => {
        this.originalValues[setting.code] = update.value;
        this.settings.find(s => s.code === setting.code).value = update.value;
        this.editingRows[setting.code] = false;
        this.settingsForm.get(setting.code)?.disable();
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Setting updated successfully'
        });
        this.savingRows[setting.code] = false;  // Clear saving state
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update setting'
        });
        this.savingRows[setting.code] = false;  // Clear saving state
      }
    });
  }

  isEditing(code: string): boolean {
    return this.editingRows[code] || false;
  }

  isSaving(code: string): boolean {
    return this.savingRows[code] || false;
  }
} 